import { RegionTypeEnum } from '@elearning-platform/api-types/enums';
import {
  CommentDTOInterface,
  CompanyStationInterface,
  CompanyWithContractSheetModel,
  ContractAcceptStatisticDTO,
  ContractDTO,
  ContractExtendedDTO,
  ContractListPaginatorDataInterface,
  ContractModelInterface,
  ContractSheetAcceptModelInterface,
  ContractSheetApproveAllModelInterface,
  ContractSheetApproveModelInterface,
  ContractStatisticsDTO,
  ContractStatisticsUserDtoInterface,
  ContractUserSheetListQueryInterface,
  ContractUserSheetModelInterface,
  IdNameGenericEntryInterface,
  IPaginatorData,
  StatusResponseInterface,
  VerifySheetModelInterface,
} from '@elearning-platform/api-types/interfaces';
import { ListPagination } from '@elearning-platform/api-types/models';
import { AxiosProgressEvent, AxiosResponse } from 'axios';
import { stringify } from 'query-string';

import {
  MyContractDetails,
  MyContractStatement,
  PagePaginationParams,
  ProvisionsContractsMonthStatsParams,
  ProvisionsContractsReportStatsParams,
  ProvisionsContractsUsersStatsParams,
} from '../../interfaces/api';
import {
  CommentCreateSimpleModel,
  CommentCreateStatus,
  ContractEditUserProfile,
  ContractStateUpdateModel,
  FetchContractsParams,
  FetchStationsParams,
  UserWithoutContract,
  UserWithoutDocumentsDetails,
} from '../../modules/provisions/interfaces';
import { api } from '../api';
import { phpApi } from '../default';

export const getMyContract: () => Promise<AxiosResponse<MyContractDetails>> = (): Promise<AxiosResponse<MyContractDetails>> =>
  phpApi.get('/api/contracts/my');

export const getContractsStatements: () => Promise<AxiosResponse<MyContractStatement[]>>
  = (): Promise<AxiosResponse<MyContractStatement[]>> => phpApi.get('/api/contracts/statements');

// todo: change namespace for provisions (on be)
export const getProvisionsCompanyWithContractSheets: (
  params: FetchStationsParams
) => Promise<ListPagination<CompanyWithContractSheetModel>> = (
  params: FetchStationsParams
): Promise<ListPagination<CompanyWithContractSheetModel>> => api.get(
  `/contracts/sheet?${stringify(params, { skipEmptyString: true })}`
);

export const getProvisionsCompanyWithContractSheet: (sheetId: number) => Promise<CompanyWithContractSheetModel> = (
  sheetId: number
): Promise<CompanyWithContractSheetModel> => api.get(
  `/contracts/sheet/${sheetId}`
);

export const getProvisionsGrouped: (
  params: ContractUserSheetListQueryInterface
) => Promise<ListPagination<ContractUserSheetModelInterface>> = (
  params: ContractUserSheetListQueryInterface
): Promise<ListPagination<ContractUserSheetModelInterface>> => api.get(
  `/contracts/sheet/grouped?${stringify(params, { skipEmptyString: true })}`
);

export const getProvisionsContracts: (params: FetchContractsParams) => Promise<ContractListPaginatorDataInterface> = (
  params: FetchContractsParams
): Promise<ContractListPaginatorDataInterface> => api.get(
  `/contracts?${stringify(params, { skipEmptyString: true })}`
);

export const getProvisionContract: (contractId: number) => Promise<ContractDTO> = (
  contractId: number
): Promise<ContractDTO> => api.get(
  `/contracts/${contractId}`
);

export const approveContractsAction: (data: ContractSheetApproveModelInterface | ContractSheetApproveAllModelInterface) => Promise<void> = (
  data: ContractSheetApproveModelInterface | ContractSheetApproveAllModelInterface
): Promise<void> => api.post(
  '/contracts/sheet/approve', data
);

export const confirmStationContractsAction: (sheetId: number, data: VerifySheetModelInterface) => Promise<void> = (
  sheetId: number, data: VerifySheetModelInterface
): Promise<void> => api.post(
  `/contracts/sheet/verify/${sheetId}`, data
);

export const confirmSheet: (sheetId: number, data: ContractSheetAcceptModelInterface) => Promise<void> = (
  sheetId: number, data: ContractSheetAcceptModelInterface
): Promise<void> => api.post(
  `/contracts/sheet/accept/${sheetId}`, data
);

// todo: update types from be package when they become available
export const editContractsStatus: (data: ContractStateUpdateModel) => Promise<void> = (
  data: ContractStateUpdateModel
): Promise<void> => api.post(
  '/contracts/state', data
);

export const editContract: (contractId: number, data: ContractModelInterface) => Promise<ContractDTO> = (
  contractId: number, data: ContractModelInterface
): Promise<ContractDTO> => api.post(
  `/contracts/${contractId}`, data
);

// todo: update types from be package when they become available
export const fetchContractUsers: (contractId: number) => Promise<ContractEditUserProfile[]> = (
  contractId: number
): Promise<ContractEditUserProfile[]> => api.get(
  `/contracts/users/${contractId}`
);

// todo: update types from be package when they become available
export const fetchUsersWithoutDocuments: (sheetId: number) => Promise<UserWithoutContract[]> = (
  sheetId: number
): Promise<UserWithoutContract[]> => api.get(
  `/contracts/sheet/user/${sheetId}`
);

export const fetchStationReport: (sheetId: number) => Promise<void> = (
  sheetId: number
): Promise<void> => api.get(
  `/contracts/sheet/generate-report/${sheetId}`, { responseType: 'blob' }
);

// todo: update types from be package when they become available
export const addContractComment: (contractId: number, data: CommentCreateSimpleModel) => Promise<CommentCreateStatus> = (
  contractId: number, data: CommentCreateSimpleModel
): Promise<CommentCreateStatus> => api.post(
  `/contracts/${contractId}/comment`, data
);

export const deleteContractComment: (contractId: number) => Promise<CommentDTOInterface[]> = (
  contractId: number
): Promise<CommentDTOInterface[]> => api.delete(
  `/contracts/${contractId}/comment`
);

// todo: update types from be package when they become available
export const fetchUserWithoutDocumentsDetails: (userId: number) => Promise<UserWithoutDocumentsDetails> = (
  userId: number
): Promise<UserWithoutDocumentsDetails> => api.get(
  `/contracts/sheet/user/details/${userId}`
);

export const fetchUserWthoutDocumentsContracts: (
  userId: number, params: PagePaginationParams
) => Promise<IPaginatorData<ContractExtendedDTO>> = (
  userId: number, params: PagePaginationParams
): Promise<IPaginatorData<ContractExtendedDTO>> => api.get(
  `/contracts/sheet/user/contracts/${userId}?${stringify(params, { skipEmptyString: true })}`
);

export const getContractsMonthStats: (params: ProvisionsContractsMonthStatsParams) => Promise<ContractStatisticsDTO> = (
  params: ProvisionsContractsMonthStatsParams
): Promise<ContractStatisticsDTO> => api.get(
  `/contracts/statistics/month?${stringify(params, { skipEmptyString: true })}`
);

export const getContractsUsersStats: (params: ProvisionsContractsUsersStatsParams) => Promise<ContractStatisticsUserDtoInterface[]> = (
  params: ProvisionsContractsUsersStatsParams
): Promise<ContractStatisticsUserDtoInterface[]> => api.get(
  `/contracts/statistics/users?${stringify(params, { skipEmptyString: true })}`
);

export const getContractsStatsReport: (params: ProvisionsContractsReportStatsParams) => Promise<void> = (
  params: ProvisionsContractsReportStatsParams
): Promise<void> => api.get(
  `/contracts/statistics/generate-report?${stringify(params, { skipEmptyString: true })}`, { responseType: 'blob' }
);

export const getSheetAcceptStats: (sheetId: number, regionType: RegionTypeEnum) => Promise<ContractAcceptStatisticDTO> = (
  sheetId: number, regionType: RegionTypeEnum
): Promise<ContractAcceptStatisticDTO> => api.get(
  `/contracts/sheet/accept-statistics/${sheetId}?${stringify({ regionType }, { skipEmptyString: true })}`
);

export const getContractsStations: () => Promise<CompanyStationInterface[]>
  = (): Promise<CompanyStationInterface[]> => api.get('/contracts/stations');

export const importProvision: (
  formData: FormData,
  setProgress: React.Dispatch<React.SetStateAction<number | undefined>>
) =>
  Promise<void> = (
    formData: FormData,
    setProgress: React.Dispatch<React.SetStateAction<number | undefined>>
  ): Promise<void> => api.post(
    '/contracts/import',
    formData,
    {
      customEnv: { notEncryptedRequest: true },
      onUploadProgress: (event: AxiosProgressEvent): void => {
        if (event.total) {
          const percent: number = Math.round((event.loaded * 100) / event.total);
          setProgress(percent);
        }
      },
    }
  );

export const getPayoutProvisionsStatus: () => Promise<StatusResponseInterface> = (): Promise<StatusResponseInterface> =>
  api.get('/contracts/reports/payout-provisions/status');

export const getPayoutProvisionsReport: () => Promise<void> = (): Promise<void> => api.get(
  '/contracts/reports/payout-provisions', { responseType: 'blob' }
);

export const getLostBenefitsReport: () => Promise<void> = (): Promise<void> => api.get(
  '/contracts/reports/lost-benefits', { responseType: 'blob' }
);

export const getInconsistencyReport: () => Promise<void> = (): Promise<void> => api.get(
  '/contracts/reports/inconsistency', { responseType: 'blob' }
);

export const addContractPrivateNote: (contractId: number, data: CommentCreateSimpleModel) => Promise<CommentDTOInterface> = (
  contractId: number, data: CommentCreateSimpleModel
): Promise<CommentDTOInterface> => api.post(
  `/contracts/note/${contractId}`, data
);

export const deleteContractPrivateNote: (contractId: number) => Promise<void> = (
  contractId: number
): Promise<void> => api.delete(
  `/contracts/note/${contractId}`
);

export const getPromotions: () => Promise<IdNameGenericEntryInterface<string>[]> = (
): Promise<IdNameGenericEntryInterface<string>[]> => api.get(
  '/contracts/promotions'
);
